import { FoquzComponent } from 'Models/foquz-component';
import { fitImageToAvailableWidth } from 'Utils/image/fit-to-available-width';

const CONTAINER_CLASS = 'user-areas-selected-showcase__container';

export class ViewModel extends FoquzComponent {
  constructor(params, element) {
    super(params, element);

    // Parameters from parent
    this.imageUrl = params.imageUrl;
    this.originalImageDimensions = params.originalImageDimensions || ko.observable({ width: 0, height: 0 });

    this.areas = ko.isObservable(params.areas)
      ? params.areas
      : ko.observableArray(params.areas || []);

    this.firstClickArea = ko.isObservable(params.firstClickArea)
      ? params.firstClickArea
      : ko.observableArray(params.firstClickArea || []);

    this.enableAreas = ko.isObservable(params.enableAreas)
      ? params.enableAreas
      : ko.observable(params.enableAreas !== false);

    this.displayedImageDimensions = ko.observable({ width: 0, height: 0 });
    this.imageOffset = ko.observable({ x: 0, y: 0 });

    this.containerElement = null;
    this.viewportElement = null;

    this.initializeComponent();
  }

  initializeComponent() {
    setTimeout(() => {
      this.containerElement = this.element.querySelector(`.${CONTAINER_CLASS}`);
      this.viewportElement = this.element.closest('.foquz-dialog__scroll') || this.element.parentElement;
      if (this.containerElement) {
        this.setupResizeObserver();
        this.updateDisplayedDimensions();
      }
    }, 0);

    if (ko.isObservable(this.originalImageDimensions)) {
      this.originalImageDimensions.subscribe(() => {
        this.updateDisplayedDimensions();
      });
    }

    if (ko.isObservable(this.firstClickArea)) {
      this.firstClickArea.subscribe(() => {
        this.updateDisplayedDimensions();
      });
    }

    if (ko.isObservable(this.enableAreas)) {
      this.enableAreas.subscribe(() => {
        this.updateDisplayedDimensions();
      });
    }

    if (ko.isObservable(this.imageUrl)) {
      this.imageUrl.subscribe(() => {
        this.updateDisplayedDimensions();
      });
    }
  }

  setupResizeObserver() {
    const observed = this.viewportElement || this.containerElement;
    if (!observed) return;

    const resizeObserver = new ResizeObserver(() => {
      this.updateDisplayedDimensions();
    });

    resizeObserver.observe(observed);
  }

  updateDisplayedDimensions() {
    if (!this.containerElement) return;

    const original = this.originalImageDimensions();
    const viewport = this.viewportElement || this.containerElement;

    if (original.width === 0 || original.height === 0) {
      this.displayedImageDimensions({ width: 0, height: 0 });
      this.imageOffset({ x: 0, y: 0 });
      return;
    }

    const fitted = fitImageToAvailableWidth(original, viewport.clientWidth);
    this.displayedImageDimensions({
      width: fitted.width,
      height: fitted.height
    });
    this.imageOffset({ x: 0, y: 0 });
  }

  calculatePixelX(proportionalX) {
    const displayed = this.displayedImageDimensions();
    return (proportionalX || 0) * displayed.width;
  }

  calculatePixelY(proportionalY) {
    const displayed = this.displayedImageDimensions();
    return (proportionalY || 0) * displayed.height;
  }

  calculatePixelWidth(proportionalWidth) {
    const displayed = this.displayedImageDimensions();
    return (proportionalWidth || 0) * displayed.width;
  }

  calculatePixelHeight(proportionalHeight) {
    const displayed = this.displayedImageDimensions();
    return (proportionalHeight || 0) * displayed.height;
  }

  getAreaNameStyles(area) {
    const areaX = this.calculatePixelX(ko.unwrap(area.x()));
    const areaY = this.calculatePixelY(ko.unwrap(area.y()));
    const areaWidth = this.calculatePixelWidth(ko.unwrap(area.width()));
    const areaHeight = this.calculatePixelHeight(ko.unwrap(area.height()));
    const imageOffset = this.imageOffset();
    const displayedDimensions = this.displayedImageDimensions();

    const labelHeight = 90; // Fixed height of the tooltip
    const labelWidth = 257; // Fixed width of the tooltip
    const labelPadding = 9; // Padding between area and tooltip
    const labelOffset = 3; // Small offset for alignment
    const edgeShift = 5; // Shift from edge

    // Image boundaries relative to the container
    const imageLeftBound = imageOffset.x;
    const imageRightBound = imageOffset.x + displayedDimensions.width;
    const imageTopBound = imageOffset.y;
    const imageBottomBound = imageOffset.y + displayedDimensions.height;

    // Available space within image boundaries
    const availableSpaceRight = imageRightBound - (areaX + imageOffset.x + areaWidth);
    const availableSpaceLeft = areaX + imageOffset.x - imageLeftBound;
    const availableSpaceBottom = imageBottomBound - (areaY + imageOffset.y + areaHeight);
    const availableSpaceTop = areaY + imageOffset.y - imageTopBound;

    // Flags to check if tooltip fits
    const canPlaceBelow = availableSpaceBottom >= labelHeight + labelPadding;
    const canPlaceAbove = availableSpaceTop >= labelHeight + labelPadding;
    const canPlaceRight = availableSpaceRight >= labelWidth + labelPadding;
    const canPlaceLeft = availableSpaceLeft >= labelWidth + labelPadding;

    let top, bottom, left, right;

    // Helper function to clamp position within image bounds
    const clampToImageBounds = (pos, max, size) => {
      return Math.max(0, Math.min(pos, max - size));
    };

    if (canPlaceBelow) {
      // Place below the area
      top = clampToImageBounds(areaHeight + labelPadding, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      bottom = 'auto';

      if (canPlaceRight) {
        left = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        right = 'auto';
      } else if (canPlaceLeft) {
        right = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        left = 'auto';
      } else {
        // Center horizontally, ensure it stays within image
        left = clampToImageBounds(
          areaWidth / 2 - labelWidth / 2,
          displayedDimensions.width - labelWidth,
          labelWidth
        ) + 'px';
        right = 'auto';
      }
    } else if (canPlaceAbove) {
      // Place above the area
      bottom = clampToImageBounds(areaHeight + labelPadding, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      top = 'auto';

      if (canPlaceRight) {
        left = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        right = 'auto';
      } else if (canPlaceLeft) {
        right = clampToImageBounds(-labelOffset + edgeShift, displayedDimensions.width - labelWidth, labelWidth) + 'px';
        left = 'auto';
      } else {
        left = clampToImageBounds(
          areaWidth / 2 - labelWidth / 2,
          displayedDimensions.width - labelWidth,
          labelWidth
        ) + 'px';
        right = 'auto';
      }
    } else if (canPlaceRight) {
      // Place to the right of the area
      left = clampToImageBounds(areaWidth + labelPadding, displayedDimensions.width - labelWidth, labelWidth) + 'px';
      right = 'auto';
      top = clampToImageBounds(0, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      bottom = 'auto';
    } else if (canPlaceLeft) {
      // Place to the left of the area
      right = clampToImageBounds(areaWidth + labelPadding, displayedDimensions.width - labelWidth, labelWidth) + 'px';
      left = 'auto';
      top = clampToImageBounds(0, displayedDimensions.height - labelHeight, labelHeight) + 'px';
      bottom = 'auto';
    } else {
      // Fallback: Center within the area, fully constrained within image
      left = clampToImageBounds(
        areaWidth / 2 - labelWidth / 2,
        displayedDimensions.width - labelWidth,
        labelWidth
      ) + 'px';
      right = 'auto';
      top = clampToImageBounds(
        areaHeight / 2 - labelHeight / 2,
        displayedDimensions.height - labelHeight,
        labelHeight
      ) + 'px';
      bottom = 'auto';
    }

    return {
      top: top,
      bottom: bottom,
      left: left,
      right: right,
      maxWidth: Math.min(labelWidth, displayedDimensions.width) + 'px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    };
  }

  dispose() {
    super.dispose();
  }
}